/**
 * Delivery Page — External broadcaster delivery access
 *
 * Route: /delivery/:deliveryCode
 *
 * Allows broadcasters (with or without an org) to:
 * - View job details and station assignments
 * - Preview audio spots in-browser
 * - Download audio files
 * - Sign in or create an org to receive in-app delivery
 *
 * Mirrors the ReviewSpot pattern for dual-path (auth/unauth) access.
 */

import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Spinner,
  Badge,
  Table,
} from 'reactstrap';
import JSZip from 'jszip';
import { FirebaseContext } from '../../components/Firebase';
import { useAuthStore } from '../../store/auth.store';
import { useOrganizationStore } from '../../store/organization.store';
import { getCampaignName } from '../../utils/campaign';
import WavePlayer from '../../components/WavePlayer';
import * as ROUTES from '../../constants/routes';

interface DeliveryData {
  deliveryCode: string;
  orgId: string;
  jobId: string;
  deliveryId: string;
  broadcasterOrgId: string | null;
  broadcasterOrgName: string;
  campaign: string;
  client: any;
  period: string;
  sourceOrgName: string;
  status: string;
  stations: {
    station: string;
    mediaOwner: string;
    language: string;
    duration: string;
    flightingCode: string;
    linkedTrackId: string | null;
    trackName?: string;
    mp3Files?: { downloadUrl: string; storageRef: string; fileName: string }[];
    wavFiles?: { downloadUrl: string; storageRef: string; fileName: string }[];
    defaultMp3Id?: string;
  }[];
}

const DeliveryPage: React.FC = () => {
  const { deliveryCode } = useParams<{ deliveryCode: string }>();
  const history = useHistory();
  const firebase = useContext(FirebaseContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<DeliveryData | null>(null);
  const [activeTrackIdx, setActiveTrackIdx] = useState<number | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

  // Auth state
  const auth = useAuthStore((s: any) => s.auth);
  const { currentOrgId, currentOrgName, organizations } = useOrganizationStore();

  // Validate delivery code on mount
  useEffect(() => {
    const validate = async () => {
      if (!firebase || !deliveryCode) {
        setError('Invalid delivery link');
        setLoading(false);
        return;
      }

      try {
        const result = await firebase.validateDeliveryCode(deliveryCode);
        if (!result.valid) {
          setError(result.error || 'Invalid delivery link');
        } else {
          setDelivery(result.data as DeliveryData);
        }
      } catch (err: any) {
        setError(`Failed to load delivery: ${err.message}`);
      }
      setLoading(false);
    };

    validate();
  }, [firebase, deliveryCode]);

  // Auth redirect handlers — preserve delivery code in sessionStorage
  const handleSignIn = () => {
    sessionStorage.setItem('pendingDeliveryCode', deliveryCode);
    history.push(ROUTES.SIGN_IN);
  };
  const handleSignUp = () => {
    sessionStorage.setItem('pendingDeliveryCode', deliveryCode);
    history.push(ROUTES.SIGN_UP);
  };
  const handleCreateOrg = () => {
    sessionStorage.setItem('pendingDeliveryCode', deliveryCode);
    history.push(ROUTES.GET_STARTED);
  };
  const handleSelectOrg = () => {
    sessionStorage.setItem('pendingDeliveryCode', deliveryCode);
    history.push(ROUTES.SELECT_ORGANIZATION);
  };

  // Claim delivery — copy tracks + job into current org
  const handleClaimDelivery = async () => {
    if (!firebase || !delivery || !currentOrgId) return;

    setClaiming(true);
    try {
      const success = await firebase.claimDelivery(currentOrgId, delivery);
      if (success) {
        setClaimed(true);
      } else {
        setError('Failed to receive delivery. Please try again.');
      }
    } catch (err: any) {
      setError(`Failed to receive delivery: ${err.message}`);
    }
    setClaiming(false);
  };

  // Check if current org already has this delivery (either original target or claimed)
  const alreadyReceived = delivery && currentOrgId && (
    delivery.broadcasterOrgId === currentOrgId ||
    (delivery as any).claimedByOrgId === currentOrgId ||
    claimed
  );

  const getClientLabel = (client: any): string => {
    if (!client) return '--';
    if (typeof client === 'object') return client.label || '--';
    return client;
  };

  const getDefaultMp3Url = (station: any): string | null => {
    if (!station.mp3Files || station.mp3Files.length === 0) return null;
    if (station.defaultMp3Id) {
      const found = station.mp3Files.find((f: any) => f.storageRef === station.defaultMp3Id);
      if (found) return found.downloadUrl;
    }
    return station.mp3Files[0]?.downloadUrl || null;
  };

  // Download all files as zip
  const handleDownloadAll = async () => {
    if (!delivery) return;

    const stations = delivery.stations.filter(s => s.linkedTrackId);
    if (stations.length === 0) return;

    setDownloading(true);
    setDownloadProgress({ current: 0, total: stations.length });

    const zip = new JSZip();

    for (let i = 0; i < stations.length; i++) {
      const station = stations[i];
      const trackName = station.trackName || station.flightingCode || `spot_${i + 1}`;

      // Download WAV
      if (station.wavFiles && station.wavFiles.length > 0) {
        try {
          const response = await fetch(station.wavFiles[0].downloadUrl);
          const blob = await response.blob();
          zip.file(`${trackName}.wav`, blob);
        } catch (err) {
          console.warn(`Failed to download WAV for ${trackName}:`, err);
        }
      }

      // Download MP3
      const mp3Url = getDefaultMp3Url(station);
      if (mp3Url) {
        try {
          const response = await fetch(mp3Url);
          const blob = await response.blob();
          zip.file(`${trackName}.mp3`, blob);
        } catch (err) {
          console.warn(`Failed to download MP3 for ${trackName}:`, err);
        }
      }

      setDownloadProgress(p => ({ ...p, current: i + 1 }));
    }

    const campaignName = getCampaignName(delivery.campaign, 'delivery').replace(/[^a-zA-Z0-9 _-]/g, '_');
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${campaignName}.zip`;
    a.click();
    URL.revokeObjectURL(url);

    setDownloading(false);

    // Fire the public download-stamp CF so the studio-side aggregate
    // "X/Y downloaded" badge + per-delivery green ticks reflect the
    // link download. Best-effort — the user has already got their
    // files, so a stamp failure never surfaces as an error. Uses the
    // public-by-code variant that does NOT require the visitor to be
    // signed in; scopes to this ONE delivery (not the broadcaster's
    // other deliveries on the same job). See functions/index.js
    // `stampDeliveryDownloadByCode`.
    try {
      await firebase.callFunction('stampDeliveryDownloadByCode', {
        deliveryCode,
        downloadMethod: 'zip',
      });
    } catch (err) {
      console.warn('[DeliveryPage] stampDeliveryDownloadByCode failed (best effort):', err);
    }
    setDownloadProgress({ current: 0, total: 0 });
  };

  // Loading state
  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <Spinner style={{ color: 'var(--accent-1-color)', width: '3rem', height: '3rem' }} />
          <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: '1rem' }}>Loading delivery...</p>
        </div>
      </Container>
    );
  }

  // Error state
  if (error || !delivery) {
    return (
      <Container style={{ maxWidth: '600px', marginTop: '4rem' }}>
        <Card className="no-bg-card">
          <CardBody className="text-center" style={{ padding: '2rem' }}>
            <i className="tim-icons icon-alert-circle-exc" style={{ fontSize: '3rem', color: '#ff5050', marginBottom: '1rem', display: 'block' }} />
            <h4 style={{ color: '#fff' }}>Delivery Not Found</h4>
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>{error || 'This delivery link is invalid or has expired.'}</p>
            {!auth && (
              <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                <Button className="btn-primary-custom" onClick={handleSignIn}>
                  <span style={{ position: 'relative', zIndex: 2 }}>Sign In</span>
                </Button>
                <Button className="btn-info-custom" onClick={handleSignUp}>
                  <span style={{ position: 'relative', zIndex: 2 }}>Create Account</span>
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    );
  }

  const linkedStations = delivery.stations.filter(s => s.linkedTrackId);

  return (
    <Container style={{ maxWidth: '900px', marginTop: '2rem', marginBottom: '3rem' }}>
      {/* Header */}
      <Card className="no-bg-card mb-3">
        <CardBody style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Spot Delivery from {delivery.sourceOrgName}
              </div>
              <h3 style={{ color: '#fff', marginBottom: '0.25rem' }}>
                {getCampaignName(delivery.campaign)}
              </h3>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                  Client: <strong style={{ color: '#fff' }}>{getClientLabel(delivery.client)}</strong>
                </span>
                {delivery.period && (
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                    Period: <strong style={{ color: '#fff' }}>{delivery.period}</strong>
                  </span>
                )}
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                  For: <strong style={{ color: 'var(--accent-1-color)' }}>{delivery.broadcasterOrgName}</strong>
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Badge style={{
                backgroundColor: delivery.status === 'acknowledged' ? 'rgba(80, 200, 120, 0.2)' : 'rgba(255, 180, 50, 0.2)',
                color: delivery.status === 'acknowledged' ? '#50c878' : '#ffb432',
                fontSize: '0.8rem', padding: '0.4rem 0.75rem',
              }}>
                {delivery.status === 'acknowledged' ? 'Acknowledged' : 'Delivered'}
              </Badge>
              {linkedStations.length > 0 && (
                <Button
                  size="sm"
                  className="btn-success-custom"
                  onClick={handleDownloadAll}
                  disabled={downloading}
                >
                  <span style={{ position: 'relative', zIndex: 2 }}>
                    {downloading ? (
                      <><Spinner size="sm" className="mr-1" /> {downloadProgress.current}/{downloadProgress.total}</>
                    ) : (
                      <><i className="tim-icons icon-cloud-download-93 mr-1"></i>Download All</>
                    )}
                  </span>
                </Button>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Auth section — mirrors ReviewSpot pattern */}
      {!auth ? (
        /* Not authenticated: offer Sign In or Sign Up */
        <Card className="no-bg-card mb-3">
          <CardBody style={{ padding: '1rem' }}>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              Sign in or create an account to receive these spots directly in your library.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Button size="sm" className="btn-primary-custom" onClick={handleSignIn}>
                <span style={{ position: 'relative', zIndex: 2 }}>Sign In</span>
              </Button>
              <Button size="sm" className="btn-info-custom" onClick={handleSignUp}>
                <span style={{ position: 'relative', zIndex: 2 }}>Create Account</span>
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : !organizations || organizations.length === 0 ? (
        /* Authenticated but no org */
        <Card className="no-bg-card mb-3">
          <CardBody style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#ffb432', fontSize: '0.85rem' }}>
              You're signed in but don't have an organization yet. Create one to receive deliveries in your library.
            </span>
            <Button size="sm" className="btn-primary-custom" onClick={handleCreateOrg}>
              <span style={{ position: 'relative', zIndex: 2 }}>Create Organization</span>
            </Button>
          </CardBody>
        </Card>
      ) : !currentOrgId ? (
        /* Authenticated with orgs but none selected */
        <Card className="no-bg-card mb-3">
          <CardBody style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#ffb432', fontSize: '0.85rem' }}>
              Select an organization to link this delivery.
            </span>
            <Button size="sm" className="btn-primary-custom" onClick={handleSelectOrg}>
              <span style={{ position: 'relative', zIndex: 2 }}>Select Organization</span>
            </Button>
          </CardBody>
        </Card>
      ) : (
        /* Authenticated with org selected */
        <Card className="no-bg-card mb-3">
          <CardBody style={{ padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
              Signed in as <strong style={{ color: '#50c878' }}>{currentOrgName}</strong>
            </span>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {alreadyReceived ? (
                <Badge style={{ backgroundColor: 'rgba(80, 200, 120, 0.2)', color: '#50c878' }}>
                  Received in Library
                </Badge>
              ) : (
                <Button size="sm" className="btn-success-custom" onClick={handleClaimDelivery} disabled={claiming}>
                  <span style={{ position: 'relative', zIndex: 2 }}>
                    {claiming ? (
                      <><Spinner size="sm" className="mr-1" /> Receiving...</>
                    ) : (
                      <><i className="tim-icons icon-cloud-download-93 mr-1"></i>Receive in Library</>
                    )}
                  </span>
                </Button>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Station/Track list */}
      <Card className="no-bg-card">
        <CardBody>
          <h5 style={{ marginBottom: '1rem' }}>
            Spots ({linkedStations.length})
          </h5>
          <Table dark responsive size="sm" style={{ backgroundColor: 'transparent' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <th style={{ border: 'none', padding: '0.5rem' }}>Station</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Spot Name</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Language</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Duration</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Flighting Code</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Preview</th>
              </tr>
            </thead>
            <tbody>
              {delivery.stations.map((station, idx) => {
                const mp3Url = getDefaultMp3Url(station);
                const hasAudio = !!mp3Url;
                const isActive = activeTrackIdx === idx;

                return (
                  <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <td style={{ border: 'none', padding: '0.5rem', color: '#fff', fontWeight: '500' }}>
                      {station.station}
                    </td>
                    <td style={{ border: 'none', padding: '0.5rem', color: 'var(--accent-1-color)' }}>
                      {station.trackName || '--'}
                    </td>
                    <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255,255,255,0.6)' }}>
                      {Array.isArray(station.language)
                        ? station.language.map((l: any) => typeof l === 'object' ? l.label : l).join(', ')
                        : station.language || '--'}
                    </td>
                    <td style={{ border: 'none', padding: '0.5rem', color: 'rgba(255,255,255,0.6)' }}>
                      {station.duration || '--'}
                    </td>
                    <td style={{ border: 'none', padding: '0.5rem' }}>
                      <span style={{ fontFamily: 'monospace', color: 'var(--accent-1-color)', fontSize: '0.85rem' }}>
                        {station.flightingCode || '--'}
                      </span>
                    </td>
                    <td style={{ border: 'none', padding: '0.5rem' }}>
                      {hasAudio ? (
                        <button
                          onClick={() => setActiveTrackIdx(isActive ? null : idx)}
                          style={{
                            background: 'none', border: '1px solid rgba(255,255,255,0.2)',
                            color: isActive ? 'var(--accent-1-color)' : 'rgba(255,255,255,0.5)',
                            cursor: 'pointer', fontSize: '0.75rem', padding: '2px 8px', borderRadius: '4px',
                          }}
                        >
                          {isActive ? 'Close' : 'Play'}
                        </button>
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem' }}>--</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {/* Audio player for active track */}
          {activeTrackIdx !== null && delivery.stations[activeTrackIdx] && (
            <div style={{
              marginTop: '1rem', padding: '1rem',
              backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <div style={{ marginBottom: '0.5rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                Now playing: <strong style={{ color: '#fff' }}>{delivery.stations[activeTrackIdx].trackName}</strong>
                {' '}&mdash; {delivery.stations[activeTrackIdx].station}
              </div>
              <WavePlayer
                id={`previewAudioPlayer-delivery-${activeTrackIdx}`}
                file={getDefaultMp3Url(delivery.stations[activeTrackIdx])}
                noAutoplay
                isActiveTrack={true}
              />
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default DeliveryPage;
